import { Lock, PlayCircle } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useStagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga, TabsModulo, TileEspecialidad } from './componentes';
import { claveEspecialidad, iconoEspecialidad } from './especialidades';
import { useClases, type Tipo } from './queries';

/**
 * Clases y talleres (legacy `curso-enarm/clases`): cuadrícula 2×2 de especialidades + lista
 * numerada; las no vistas van en negritas y resaltadas, como la tabla original.
 * La especialidad elegida vive en la URL (`?especialidad=`) para compartir y volver atrás.
 */
export function ClasesPage({ tipo }: { tipo: Tipo }) {
  const { data, error, isPending } = useClases(tipo);
  const [params, setParams] = useSearchParams();
  const lista = useRef<HTMLDivElement>(null);
  const tiles = useRef<HTMLDivElement>(null);
  const ruta = tipo === 'class' ? '/curso-enarm/clases' : '/curso-enarm/talleres';

  const especialidades = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of data?.items ?? []) m.set(c.specialty, (m.get(c.specialty) ?? 0) + 1);
    return [...m].map(([nombre, conteo]) => ({ nombre, conteo }));
  }, [data]);
  // El menú enlaza con claves (`?especialidad=pediatria`) y los tiles con el nombre de la API.
  const pedida = params.get('especialidad');
  const elegida =
    (pedida &&
      especialidades.find((e) => claveEspecialidad(e.nombre) === claveEspecialidad(pedida))
        ?.nombre) ??
    especialidades[0]?.nombre ??
    null;
  const items = (data?.items ?? []).filter((c) => c.specialty === elegida);

  useStagger(tiles, '[data-tile]', { step: 50, deps: [especialidades.length] });
  useStagger(lista, '[data-fila]', { step: 30, y: 12, deps: [elegida, data] });

  const limite = data?.limits.classLimitEnforced ? data.limits.classes : null;

  return (
    <div className="space-y-6">
      <TabsModulo />
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">{tipo === 'class' ? 'Clases' : 'Talleres Consalud'}</span>
      </h1>
      {isPending && <Cargando />}
      {error && <ErrorCarga error={error} />}
      {data && data.items.length === 0 && (
        <section className="panel-vidrio text-center text-texto-2">
          {tipo === 'class'
            ? 'Tu licencia actual no incluye clases. Revisa las licencias disponibles.'
            : 'Aún no hay talleres disponibles para tu licencia.'}
          <div className="mt-4">
            <Link to="/licencias" className="btn-cs btn-primario">
              Ver licencias
            </Link>
          </div>
        </section>
      )}
      {data && data.items.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <div ref={tiles} className="grid grid-cols-2 gap-3 self-start">
            {especialidades.map((e) => (
              <TileEspecialidad
                key={e.nombre}
                nombre={e.nombre}
                conteo={e.conteo}
                activo={e.nombre === elegida}
                onClick={() => {
                  setParams({ especialidad: e.nombre }, { replace: true });
                }}
              />
            ))}
          </div>
          <section className="panel-vidrio p-0">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--separador)] px-5 py-4">
              <h2 className="flex items-center gap-2 text-[17px] font-extrabold text-azul-texto uppercase">
                {elegida && (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-azul-600">
                    <img src={iconoEspecialidad(elegida)} alt="" className="h-5 w-5" />
                  </span>
                )}
                {tipo === 'class' ? 'Clases de' : 'Talleres de'} {elegida?.toLowerCase()}
              </h2>
              {limite !== null && (
                <p className="text-xs text-texto-3">
                  Puedes reproducir cada {tipo === 'class' ? 'clase' : 'taller'} hasta {limite}{' '}
                  veces.
                </p>
              )}
            </header>
            <div ref={lista} role="table" aria-label={`Lista de ${elegida ?? ''}`}>
              {items.map((c) => {
                const fila = (
                  <>
                    <span
                      role="cell"
                      className="w-10 shrink-0 text-right font-extrabold text-cian-texto tabular"
                    >
                      {c.number}.-
                    </span>
                    <span
                      role="cell"
                      className={cn(
                        'flex-1 text-sm uppercase',
                        c.views > 0 ? 'font-semibold text-texto-2' : 'font-extrabold text-texto',
                      )}
                    >
                      {c.name}
                    </span>
                    <span role="cell" className="shrink-0 text-xs text-texto-3">
                      {c.views > 0
                        ? `Reproducido ${String(c.views)} ${c.views === 1 ? 'vez' : 'veces'}`
                        : 'Sin ver'}
                    </span>
                    {c.blocked ? (
                      <Lock
                        size={18}
                        className="shrink-0 text-rojo-texto"
                        aria-label="Límite alcanzado"
                      />
                    ) : (
                      <PlayCircle size={18} className="shrink-0 text-verde-texto" aria-hidden />
                    )}
                  </>
                );
                // Sin ver: resaltada como la fila `table-primary` del legacy.
                const clase = cn(
                  'flex items-center gap-3 border-b border-[var(--separador)] px-5 py-3 last:border-b-0',
                  c.views === 0 && 'bg-azul-600/10',
                );
                return c.blocked ? (
                  <div
                    key={c.id}
                    data-fila
                    role="row"
                    className={cn(clase, 'opacity-60')}
                    title={`Alcanzaste el límite de ${String(limite ?? '')} reproducciones`}
                  >
                    {fila}
                  </div>
                ) : (
                  <Link
                    key={c.id}
                    data-fila
                    role="row"
                    to={`${ruta}/${String(c.id)}`}
                    className={cn(clase, 'transition-colors hover:bg-azul-600/20')}
                  >
                    {fila}
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
